import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { useTenantConfig } from "src/main_app/actions";
import { andThen, pathOr, compose, isNil } from "ramda";
import { isNilOrEmpty } from "src/main_app/utils/common";
import {
  getPublicDataValidationTable,
  getPublicDataValidationGraph,
} from "src/main_app/services/apis";
import * as d3 from 'd3'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { TailSpin } from "react-loader-spinner"

const getValidationCustomerDisplayName = (item) => {
  if (!isNilOrEmpty(item.site_name)) return item.site_name;
  if (!isNilOrEmpty(item.trading_name)) return item.trading_name;
  if (!isNilOrEmpty(item.owner_name)) return item.owner_name;
  return item.customer_name ?? "";
};

const DataValidation = (props) => {

  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false)
  const tenantConfig = useTenantConfig();
  const [iframeUrl, setIframeUrl] = useState("");
  const [iframeHeight, setIframeHeight] = useState(2430);
  const [dataTableValidation, setDataTableValidation] = useState([]);
  const [dataChartValidation, setDataChartValidation] = useState([]);

  const loadDataValidation = async () => {
    try {
      const [tableResponse, graphResponse] = await Promise.all([
        getPublicDataValidationTable(),
        getPublicDataValidationGraph(),
      ]);

      setDataTableValidation(tableResponse?.data?.resTable ?? []);
      setDataChartValidation(graphResponse?.data?.resGraph ?? []);
    } finally {
      setLoading(false);
    }
  };

  const yFormatter = (value, index) =>
    (Math.round(value/1000)).toLocaleString() + ' kL'; // L Convert to kL

  const formatDataKL = (value) => {
    return (Math.round(value/1000)).toLocaleString() + ' kL';
  }

  const formatLogMonth = (value) => {
    if (!value) return value;
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  };

  const yLabel = useMemo(
    () => ({
      value: "Water usage difference",
      angle: -90,
      position: "top",
      dy: 170,
      dx: -20,
    }),
    []
  );

  const defaultPageDetail = useMemo(
    () => (tenantConfig.slug === "intellihub" ? "usage" : "overview"),
    [tenantConfig.slug]
  );


const renderTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  console.log("payload", payload);
  return (
    <div
      className="custom-line-tooltip"
      style={{
        padding: "10px",
        backgroundColor: "#fff",
        boxShadow: "0 0 8px 8px rgb(0 0 0 / 7%)",
        borderRadius: "4px",
      }}
    >
      <h5 className="mb-2"><strong>{payload[0].payload.customer_name}</strong></h5>
      <p className="mb-0">Consumption difference: <strong>{Math.round((payload[0].payload['Consumption difference']/1000)).toLocaleString()} kL</strong></p>
    </div>
  );
};

  useEffect(() => {
    import("./style.scss");
    setLoading(true)
    setIframeUrl(pathOr("", ["insightsPageTabDataValidation", "iframeUrl"], tenantConfig));
    setIframeHeight(
      pathOr(2430, ["insightsPageTabDataValidation", "iframeHeight"], tenantConfig)
    );
    loadDataValidation();
  }, [tenantConfig]);

  return (
    <>

    <div className="container-fluid content">
        {/* <iframe
          style={{
            width: "100%",
            height: iframeHeight + "px",
          }}
          scrolling="no"
          frameBorder="0"
          src={iframeUrl}
        /> */}

        {/* Start Data table validation  */}
        <div className="table-responsive">
        <h3 className="sub-title">Water consumption data validation</h3>
          <div className="overflow-auto" style={{maxHeight: "500px"}}>
            <table className="table" id="user-list-table">
                <thead style={{position: "sticky", top: 0}}>
                    <tr>
                        <th>Customer</th>
                        <th>Log month</th>
                        <th>Real-time consumption</th>
                        <th>Metered consumption</th>
                        <th>Consumption difference</th>
                    </tr>
                </thead>
                <tbody>
                {(dataTableValidation || []).map((item, index) => (
                    <tr key={index}>
                        <td>
                          {item.customer_id ? (
                            <Link
                              to={`/customer/${item.customer_id}/${defaultPageDetail}`}
                              style={{ color: "#000" }}
                            >
                              {getValidationCustomerDisplayName(item)}
                            </Link>
                          ) : (
                            getValidationCustomerDisplayName(item)
                          )}
                        </td>
                        <td>{formatLogMonth(item.log_month)}</td>
                        <td>{formatDataKL(item['Real-time consumption'])}</td>
                        <td>{formatDataKL(item['Metered consumption'])}</td>
                        <td>{formatDataKL(item['Consumption difference'])}</td>
                    </tr>
                ))}
                </tbody>
            </table>
          </div>
        </div>
        {/* End Data table validation  */}

        {/* Start Chart data validation  */}
        <br></br>
        <div className="mt-5">
          <h3 className="sub-title">Top 10 customers with largest cumulative water usage difference</h3>
            <div className="card" id="metered-chart">
              <div className="card-body">
                {dataChartValidation.length > 0 && (
                  <ResponsiveContainer width="100%" aspect={10.0 / 3.0}>
                  <BarChart
                    data={dataChartValidation}
                    margin={{
                      top: 20,
                      right: 5,
                      left: -15,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid stroke="#e9ebf1" />
                    <XAxis
                      dataKey="customer_name"
                      stroke="#e9ebf1"
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      height={140}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      dataKey={"Consumption difference"}
                      stroke="#e9ebf1"
                      tickFormatter={yFormatter}
                      width={110}
                      label={yLabel}
                    />
                    <Tooltip 
                     content={renderTooltip}
                    />
                    <Bar
                      dataKey={"Consumption difference"}
                      fill="#09A3B3"
                      radius={[5, 5, 0, 0]}
                      barSize={24}
                      // unit="ML"
                    />
                  </BarChart>
                </ResponsiveContainer>
                )}
                
              </div>
            </div>
        </div>
        {/* End Chart data validation  */}
      </div>
    </>
  );
};

export default DataValidation;
